/**
 * AdminFoundationsUpload.jsx
 *
 * Feb 11 2026 · Michael's Dropbox intake path.
 *
 * Drag-drop admin page for uploading Soulumo Foundations audio.
 * Each row is one Foundations catalogue item. Dropping an audio
 * file (or picking one via the file input) POSTs it to
 * /api/foundations/admin/upload — the backend writes to Emergent
 * Object Storage and flips the item's audio_status to "ready"
 * automatically. Old takes for the same item are soft-deleted.
 *
 * Route: /admin/foundations/upload   (admin only)
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminLayout } from "../AdminPages";
import { ArrowLeft, CheckCircle2, Circle, Loader2, Trash2, UploadCloud } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` },
});

const STATUS_STYLE = {
  ready: { label: "Audio attached", tone: "text-emerald-600", Icon: CheckCircle2 },
  script_approved: { label: "Script approved · audio needed", tone: "text-amber-600", Icon: Circle },
  needs_recording: { label: "Awaiting script + audio", tone: "text-stone-400", Icon: Circle },
};

function humanBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminFoundationsUpload() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [byItem, setByItem] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/api/foundations/admin/uploads`, auth());
      setItems(data.items || []);
      setByItem(data.by_item || {});
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "admin_only") {
        setErr("Admin access required.");
      } else {
        setErr("Failed to load uploads.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const readyCount = items.filter((i) => byItem[i.id]?.length).length;

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto px-5 py-8">
        <button
          onClick={() => nav("/admin")}
          data-testid="admin-foundations-back"
          className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 mb-6"
        >
          <ArrowLeft size={14} /> Back to admin
        </button>

        <header className="mb-8">
          <div className="text-[0.65rem] uppercase tracking-[0.28em] text-stone-400 mb-1">
            ✦ Soulumo Foundations · Audio Intake ✦
          </div>
          <h1 className="text-2xl font-light" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            Upload approved Foundations audio
          </h1>
          <p className="mt-2 text-sm text-stone-500 leading-relaxed">
            Drag &amp; drop a WAV, MP3 or M4A file onto a row to attach it.
            The file goes straight to Emergent Object Storage and the
            catalogue item flips to <span className="text-emerald-600 font-medium">ready</span> the moment the upload succeeds.
            Uploading a new take auto-retires the previous one.
          </p>
          {!loading && (
            <div
              className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full"
              style={{ background: "rgba(15,80,50,0.08)", color: "#1a5a3a", border: "1px solid rgba(15,80,50,0.2)" }}
              data-testid="admin-foundations-progress"
            >
              <CheckCircle2 size={12} /> {readyCount} / {items.length} items have audio attached
            </div>
          )}
        </header>

        {err && (
          <div
            className="mb-6 px-4 py-3 rounded-xl text-sm"
            style={{ background: "rgba(200,80,60,0.08)", color: "#8a2b1a", border: "1px solid rgba(200,80,60,0.25)" }}
            data-testid="admin-foundations-error"
          >
            {err}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-stone-400">
            <Loader2 size={14} className="animate-spin" /> Loading catalogue…
          </div>
        ) : (
          <ul className="space-y-3" data-testid="admin-foundations-list">
            {items.map((it) => (
              <li key={it.id} data-testid={`admin-foundations-row-${it.id}`}>
                <ItemRow
                  item={it}
                  uploads={byItem[it.id] || []}
                  onChanged={load}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}

function ItemRow({ item, uploads, onChanged }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadErr, setUploadErr] = useState("");
  const live = uploads[0]; // list is uploaded_at DESC — first is latest
  const effectiveStatus = live ? "ready" : item.audio_status;
  const style = STATUS_STYLE[effectiveStatus] || STATUS_STYLE.needs_recording;
  const StatusIcon = style.Icon;

  const doUpload = useCallback(async (file) => {
    setUploading(true);
    setProgress(0);
    setUploadErr("");
    try {
      const form = new FormData();
      form.append("item_id", item.id);
      form.append("file", file);
      await axios.post(
        `${API}/api/foundations/admin/upload`,
        form,
        {
          ...auth(),
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
          timeout: 300000, // 5-min for ~250 MB files
        },
      );
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "upload_failed";
      setUploadErr(String(detail));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [item.id, onChanged]);

  const handleDrop = (ev) => {
    ev.preventDefault();
    setDragOver(false);
    const f = ev.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const handleDelete = async (uid) => {
    if (!window.confirm("Retire this upload?")) return;
    try {
      await axios.delete(`${API}/api/foundations/admin/upload/${uid}`, auth());
      onChanged?.();
    } catch (e) {
      console.warn("delete failed:", e);
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className="rounded-2xl p-5 transition-all"
      style={{
        background: dragOver ? "rgba(120,90,60,0.06)" : "rgba(245,240,232,0.6)",
        border: dragOver ? "2px dashed #b8935b" : "1px solid rgba(120,90,60,0.18)",
      }}
    >
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-medium text-stone-800">{item.label}</h3>
            <span className={`inline-flex items-center gap-1 text-xs ${style.tone}`}>
              <StatusIcon size={12} /> {style.label}
            </span>
            {item.duration_minutes ? (
              <span className="text-xs text-stone-400">· {item.duration_minutes} min</span>
            ) : null}
            {item.shared && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase px-2 py-0.5 rounded-full"
                style={{
                  background: "rgba(80,60,140,0.10)",
                  color: "#5a3d99",
                  border: "1px solid rgba(80,60,140,0.32)",
                  letterSpacing: "0.22em",
                  fontWeight: 600,
                }}
                title="Rendered once · streamed from cache to every member"
                data-testid={`admin-foundations-shared-${item.id}`}
              >
                ◇ Shared master
              </span>
            )}
          </div>
          {item.blurb && (
            <p className="mt-1 text-sm text-stone-500 italic leading-relaxed">
              {item.blurb}
            </p>
          )}
          {live && (
            <div
              className="mt-3 flex items-center gap-3 rounded-xl px-3 py-2"
              style={{ background: "rgba(15,80,50,0.06)", border: "1px solid rgba(15,80,50,0.14)" }}
              data-testid={`admin-foundations-live-${item.id}`}
            >
              <audio controls preload="none" src={`${API}${live.audio_url}`} className="flex-1" />
              <div className="text-xs text-stone-600 min-w-0">
                <div className="truncate">{live.filename}</div>
                <div className="text-stone-400">
                  {humanBytes(live.size_bytes)} · uploaded {live.uploaded_at?.slice(0, 10)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(live.id)}
                aria-label="Retire this upload"
                className="p-2 rounded-lg text-stone-400 hover:text-red-500"
                data-testid={`admin-foundations-delete-${item.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
          {uploadErr && (
            <p className="mt-2 text-xs text-red-500" data-testid={`admin-foundations-uploaderr-${item.id}`}>
              {uploadErr}
            </p>
          )}
        </div>

        <div className="shrink-0 flex flex-col items-end gap-2">
          <label
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 cursor-pointer text-xs font-medium transition-colors"
            style={{
              background: uploading ? "rgba(120,90,60,0.14)" : "#b8935b",
              color: uploading ? "#7a5a34" : "#fff",
              minHeight: 40,
            }}
            data-testid={`admin-foundations-picker-${item.id}`}
          >
            {uploading ? (
              <>
                <Loader2 size={12} className="animate-spin" /> {progress}%
              </>
            ) : (
              <>
                <UploadCloud size={13} /> {live ? "Replace audio" : "Upload audio"}
              </>
            )}
            <input
              type="file"
              accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) doUpload(f);
                e.target.value = "";
              }}
              disabled={uploading}
            />
          </label>
          <span className="text-[10px] uppercase tracking-[0.22em] text-stone-400">
            or drop a file
          </span>
        </div>
      </div>
    </div>
  );
}
